import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import StaffPageShell from '../components/StaffPageShell';
import MessageDisplay from '../components/MessageDisplay';
import { ThemeButton } from '../components/ThemeWrapper';
import usePermissions from '../hooks/usePermissions';
import useDatabaseBackups from '../hooks/useDatabaseBackups';
import {
  DATABASE_BACKUP_HELP,
  MAX_RETAINED_BACKUPS,
  RESTORE_CONFIRM_PHRASE,
  formatBackupSize,
} from '../domain/system/databaseBackupCopy';
import '../styles/notifications.css';

export default function AdminDatabaseBackupsPage() {
  const { hasPermission } = usePermissions();
  const enabled = hasPermission('system.backup');
  const { data, loading, busy, error, success, snapshot, upload, restore, remove, download } =
    useDatabaseBackups({ enabled });
  const [note, setNote] = useState('');
  const [file, setFile] = useState(null);
  const [restoreId, setRestoreId] = useState('');
  const [confirm, setConfirm] = useState('');

  if (!enabled) {
    return <Navigate to="/dashboard" replace />;
  }

  const backups = data?.backups || [];
  const toolsOk = data?.toolsAvailable !== false;
  const restoring = String(busy).startsWith('restore:');

  return (
    <StaffPageShell
      title={DATABASE_BACKUP_HELP.title}
      subtitle={DATABASE_BACKUP_HELP.subtitle}
      wide
    >
      <p className="staff-page-lead">{DATABASE_BACKUP_HELP.warn}</p>
      <MessageDisplay error={error} success={success} />

      {!toolsOk ? (
        <p className="notif-muted">
          pg_dump / pg_restore are missing on the API host. Snapshot and restore stay disabled until
          postgresql-client is installed.
        </p>
      ) : null}

      <section className="notif-panel" style={{ marginBottom: '1rem' }}>
        <h2 style={{ margin: '0 0 0.75rem', fontSize: '1.05rem' }}>
          Restore points ({backups.length} / {data?.max || MAX_RETAINED_BACKUPS})
        </h2>
        {loading ? <p className="notif-muted">Loading…</p> : null}
        {!loading && backups.length === 0 ? (
          <p className="notif-muted">No restore points yet. Snapshot now or upload a dump.</p>
        ) : null}
        <div style={{ display: 'grid', gap: '0.75rem' }}>
          {backups.map((row) => (
            <div
              key={row.id}
              style={{
                display: 'grid',
                gap: '0.35rem',
                paddingBottom: '0.75rem',
                borderBottom: '1px solid var(--color-border)',
              }}
            >
              <div style={{ fontWeight: 600 }}>
                Slot {row.slot}: {row.originalName}
              </div>
              <div className="notif-muted" style={{ fontSize: '0.85rem' }}>
                {row.source} · {formatBackupSize(row.sizeBytes)} ·{' '}
                {row.createdAt ? new Date(row.createdAt).toLocaleString() : '—'}
                {row.createdByEmail ? ` · ${row.createdByEmail}` : ''}
                {row.note ? ` · ${row.note}` : ''}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                <ThemeButton
                  type="button"
                  size="sm"
                  disabled={Boolean(busy)}
                  onClick={() => {
                    setRestoreId(row.id);
                    setConfirm('');
                  }}
                >
                  Roll back to this
                </ThemeButton>
                <ThemeButton
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={Boolean(busy)}
                  onClick={() => download(row.id, row.originalName)}
                >
                  Download
                </ThemeButton>
                <ThemeButton
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={Boolean(busy)}
                  onClick={() => {
                    if (window.confirm('Remove this restore point from the five slots?')) {
                      remove(row.id);
                    }
                  }}
                >
                  Delete
                </ThemeButton>
              </div>
              {restoreId === row.id ? (
                <div style={{ display: 'grid', gap: '0.4rem', maxWidth: '28rem' }}>
                  <label className="notif-muted" htmlFor={`confirm-${row.id}`}>
                    Type {RESTORE_CONFIRM_PHRASE} to replace all current data with this backup
                  </label>
                  <input
                    id={`confirm-${row.id}`}
                    className="theme-input"
                    value={restoreId === row.id ? confirm : ''}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder={RESTORE_CONFIRM_PHRASE}
                    autoComplete="off"
                  />
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <ThemeButton
                      type="button"
                      size="sm"
                      variant="warning"
                      disabled={restoring}
                      onClick={async () => {
                        const ok = await restore(row.id, confirm);
                        if (ok) {
                          setRestoreId('');
                          setConfirm('');
                        }
                      }}
                    >
                      {restoring ? 'Restoring…' : 'Confirm rollback'}
                    </ThemeButton>
                    <ThemeButton
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={restoring}
                      onClick={() => {
                        setRestoreId('');
                        setConfirm('');
                      }}
                    >
                      Cancel
                    </ThemeButton>
                  </div>
                </div>
              ) : null}
            </div>
          ))}
        </div>
      </section>

      <section className="notif-panel">
        <h2 style={{ margin: '0 0 0.75rem', fontSize: '1.05rem' }}>Add a restore point</h2>
        <label className="notif-muted" htmlFor="backup-note">
          Optional note
        </label>
        <input
          id="backup-note"
          className="theme-input"
          style={{ maxWidth: '28rem', display: 'block', margin: '0.35rem 0 0.75rem' }}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Before VE import, 15 Sep"
        />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center' }}>
          <ThemeButton
            type="button"
            disabled={Boolean(busy) || !toolsOk}
            onClick={() => snapshot(note)}
          >
            {busy === 'snapshot' ? 'Saving…' : 'Snapshot current database'}
          </ThemeButton>
          <input
            type="file"
            accept=".dump,.backup,.sql"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
          <ThemeButton
            type="button"
            disabled={Boolean(busy) || !file}
            onClick={() => upload(file, note)}
          >
            {busy === 'upload' ? 'Uploading…' : 'Upload dump'}
          </ThemeButton>
        </div>
      </section>
    </StaffPageShell>
  );
}
